import type { InputHTMLAttributes, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cx } from "./cx";
import { VISUALLY_HIDDEN_INPUT } from "./fieldParts";

export interface ChoiceCardProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "children"> {
  /** Drawn in a tinted square beside the radio. */
  icon?: LucideIcon;
  /** The choice itself: its name, then one short line. */
  children: ReactNode;
  /** Cost and timing, on the right; on a narrow screen it drops under the children. */
  aside?: ReactNode;
}

/**
 * One choice of several, as a whole bordered box (checkout, KALI-24). Picked shows the dot, a
 * thicker green border and a tint, never colour alone; keyboard focus shows the gold ring.
 */
export function ChoiceCard({ icon: IconComponent, children, aside, className, ...rest }: ChoiceCardProps) {
  return (
    <label
      className={cx(
        "group relative flex min-h-13 cursor-pointer items-start gap-3 rounded-md border border-border-default bg-surface-card px-4 py-3.5 shadow-xs",
        "has-checked:border-green-700 has-checked:bg-surface-tint-green has-checked:inset-ring-1 has-checked:inset-ring-green-700",
        "has-focus-visible:shadow-focus",
        "has-disabled:cursor-not-allowed has-disabled:border-dashed has-disabled:bg-surface-sunken has-disabled:shadow-none",
        className,
      )}
    >
      <input type="radio" className={VISUALLY_HIDDEN_INPUT} {...rest} />
      <span
        aria-hidden="true"
        className={cx(
          "flex size-5 flex-none items-center justify-center rounded-pill border border-border-strong bg-surface-card inset-shadow-default",
          IconComponent ? "mt-2.5" : "mt-0.5",
          "peer-checked:border-green-700 peer-checked:inset-shadow-none peer-disabled:border-ink-300 peer-disabled:bg-paper-3",
          "[&>span]:invisible peer-checked:[&>span]:visible",
        )}
      >
        <span className="size-2.5 rounded-pill bg-green-700" />
      </span>
      {IconComponent && (
        <span
          aria-hidden="true"
          className="flex size-10 flex-none items-center justify-center rounded-md bg-surface-tint-green text-green-700 group-has-checked:bg-surface-card group-has-disabled:bg-paper-3 group-has-disabled:text-ink-400"
        >
          <IconComponent size={22} strokeWidth={1.8} />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-wrap items-start gap-x-4 gap-y-1.5">
        <span className="flex min-w-0 flex-[999_1_220px] flex-col gap-0.5">{children}</span>
        {aside && <span className="flex flex-[1_0_200px] flex-col gap-0.5">{aside}</span>}
      </span>
    </label>
  );
}
